import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { NivaMobileCard, NivaNavPill } from "./niva-nav-cta";
import { ThemeToggle } from "@/components/theme/theme-provider";

const LOGO_ON_LIGHT = "/logos/nivalabs-on-light.png";
const LOGO_ON_DARK = "/logos/nivalabs-on-dark.png";

const SERVICE_LINKS = [
  { href: "/ai-agent-development", label: "AI Agent Development" },
  {
    href: "/custom-llm-application-development",
    label: "Custom LLM Applications",
  },
  { href: "/ai-workflow-automation", label: "AI Workflow Automation" },
  { href: "/ai-integrations", label: "AI Integrations" },
  { href: "/mvp-poc-development", label: "MVP & PoC Development" },
];

const SOLUTION_LINKS = [
  {
    href: "/solutions/enterprise-ai-chatbot-development",
    label: "Enterprise chatbots",
  },
  {
    href: "/solutions/ai-knowledge-assistants-for-organizations",
    label: "Knowledge assistants",
  },
  {
    href: "/solutions/ai-data-intelligence-platforms-for-businesses",
    label: "Data intelligence platforms",
  },
  {
    href: "/solutions/ai-solutions-for-logistics-operations",
    label: "Logistics & supply chain",
  },
  {
    href: "/solutions/ai-solutions-for-marina-management",
    label: "Marina & ports",
  },
];

function pathMatches(router, href) {
  const path = router.asPath.split("?")[0];
  if (path === href) return true;
  if (href !== "/" && path.startsWith(`${href}/`)) return true;
  return false;
}

function chevronClass(open) {
  return `ml-0.5 inline-block h-4 w-4 shrink-0 transition-transform duration-200 ${
    open ? "rotate-180" : ""
  }`;
}

function NavLink({ href, children, className = "" }) {
  const router = useRouter();
  const active = pathMatches(router, href);
  return (
    <Link
      href={href}
      className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? "text-[#1c8c80]"
          : "text-ink-muted hover:text-ink"
      } ${className}`}
    >
      {children}
    </Link>
  );
}

const Header = () => {
  const router = useRouter();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const rootRef = useRef(null);
  const mobileNavId = useId();

  const servicesActive = SERVICE_LINKS.some((l) => pathMatches(router, l.href));
  const solutionsActive =
    pathMatches(router, "/solutions") ||
    SOLUTION_LINKS.some((l) => pathMatches(router, l.href));

  const closeMenus = useCallback(() => {
    setOpenMenu(null);
  }, []);

  useEffect(() => {
    if (!openMenu) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openMenu]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  useEffect(() => {
    const onRoute = () => {
      setOpenMenu(null);
      setMobileOpen(false);
      setMobileSection(null);
    };
    router.events.on("routeChangeComplete", onRoute);
    return () => router.events.off("routeChangeComplete", onRoute);
  }, [router.events]);

  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, []);

  return (
    <header
      ref={rootRef}
      className="sticky top-0 z-50 isolate border-b border-line bg-surface text-ink shadow-[0_8px_30px_-18px_rgba(15,28,26,0.22)]"
    >
      <nav
        className="container mx-auto flex max-w-7xl items-center justify-between gap-3 py-4 md:py-5"
        aria-label="Main"
      >
        <Link
          href="/"
          className="relative z-[60] flex shrink-0 items-center"
          onClick={() => setMobileOpen(false)}
        >
          <Image
            src={LOGO_ON_LIGHT}
            alt="Nivalabs"
            width={190}
            height={30}
            priority
            fetchPriority="high"
            className="theme-logo-light h-[1.125rem] w-auto sm:h-5"
          />
          <Image
            src={LOGO_ON_DARK}
            alt="Nivalabs"
            width={190}
            height={30}
            priority
            fetchPriority="high"
            className="theme-logo-dark h-[1.125rem] w-auto sm:h-5"
          />
        </Link>

        <div className="hidden flex-1 items-center justify-center gap-0.5 px-2 lg:flex">
          <div className="relative">
            <button
              type="button"
              className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                openMenu === "services" || servicesActive
                  ? "text-[#1c8c80]"
                  : "text-ink-muted hover:text-ink"
              }`}
              aria-expanded={openMenu === "services"}
              aria-haspopup="true"
              onClick={() =>
                setOpenMenu((m) => (m === "services" ? null : "services"))
              }
            >
              Services
              <svg
                className={chevronClass(openMenu === "services")}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" />
              </svg>
            </button>
            {openMenu === "services" && (
              <ul
                className="absolute left-0 top-full z-[70] mt-1 min-w-[16.5rem] list-none rounded-xl border border-line bg-surface py-2 shadow-[0_20px_50px_-24px_rgba(15,28,26,0.35)]"
                role="menu"
              >
                {SERVICE_LINKS.map((item) => {
                  const active = pathMatches(router, item.href);
                  return (
                    <li key={item.href} role="none">
                      <Link
                        href={item.href}
                        role="menuitem"
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          active
                            ? "bg-muted text-[#1c8c80]"
                            : "text-ink-muted hover:bg-muted hover:text-ink"
                        }`}
                        onClick={closeMenus}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              className={`flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                openMenu === "solutions" || solutionsActive
                  ? "text-[#1c8c80]"
                  : "text-ink-muted hover:text-ink"
              }`}
              aria-expanded={openMenu === "solutions"}
              aria-haspopup="true"
              onClick={() =>
                setOpenMenu((m) => (m === "solutions" ? null : "solutions"))
              }
            >
              Solutions
              <svg
                className={chevronClass(openMenu === "solutions")}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden
              >
                <path d="M6 9l6 6 6-6" strokeLinecap="round" />
              </svg>
            </button>
            {openMenu === "solutions" && (
              <ul
                className="absolute left-0 top-full z-[70] mt-1 min-w-[17.5rem] list-none rounded-xl border border-line bg-surface py-2 shadow-[0_20px_50px_-24px_rgba(15,28,26,0.35)]"
                role="menu"
              >
                {SOLUTION_LINKS.map((item) => {
                  const active = pathMatches(router, item.href);
                  return (
                    <li key={item.href} role="none">
                      <Link
                        href={item.href}
                        role="menuitem"
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          active
                            ? "bg-muted text-[#1c8c80]"
                            : "text-ink-muted hover:bg-muted hover:text-ink"
                        }`}
                        onClick={closeMenus}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
                <li role="none" className="mt-1 border-t border-line pt-1">
                  <Link
                    href="/solutions"
                    role="menuitem"
                    className="block px-4 py-2.5 text-sm font-semibold text-[#1c8c80] transition-colors hover:bg-muted"
                    onClick={closeMenus}
                  >
                    View all solutions
                  </Link>
                </li>
              </ul>
            )}
          </div>

          <NavLink href="/use-cases">Use cases</NavLink>
          <NavLink href="/how-we-work">How we work</NavLink>
          <NavLink href="/blogs">Blog</NavLink>
          <NavLink href="/about">About</NavLink>
          <NavLink href="/contact">Contact</NavLink>
        </div>

        <div className="flex items-center gap-5 sm:gap-6">
          <ThemeToggle className="hidden sm:inline-flex" />
          <NivaNavPill />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-line text-ink-muted hover:border-[#1c8c80] hover:text-ink lg:hidden"
            aria-expanded={mobileOpen}
            aria-controls={mobileNavId}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => {
              setMobileOpen((o) => !o);
              setOpenMenu(null);
            }}
          >
            {mobileOpen ? (
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            ) : (
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M4 7h16M4 12h16M4 17h10"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-[#0f1c1a]/35 lg:hidden"
          role="presentation"
          onClick={() => setMobileOpen(false)}
        >
          <div
            id={mobileNavId}
            className="h-full w-full max-w-sm overflow-y-auto border-l border-line bg-surface shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 pt-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-ink">Menu</span>
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <button
                    type="button"
                    className="rounded-lg p-2 text-ink-soft hover:text-ink"
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close"
                  >
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        d="M18 6L6 18M6 6l12 12"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <NivaMobileCard onNavigate={() => setMobileOpen(false)} />

              <div className="space-y-1">
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base font-medium text-ink hover:bg-muted"
                  onClick={() =>
                    setMobileSection((s) =>
                      s === "services" ? null : "services",
                    )
                  }
                >
                  Services
                  <svg
                    className={chevronClass(mobileSection === "services")}
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" />
                  </svg>
                </button>
                {mobileSection === "services" && (
                  <ul className="ml-1 space-y-0.5 border-l-2 border-[#1c8c80]/30 pl-3">
                    {SERVICE_LINKS.map((item) => (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          className="block rounded-md py-2.5 pl-1 text-sm text-ink-muted hover:text-ink"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-3 py-3 text-left text-base font-medium text-ink hover:bg-muted"
                  onClick={() =>
                    setMobileSection((s) =>
                      s === "solutions" ? null : "solutions",
                    )
                  }
                >
                  Solutions
                  <svg
                    className={chevronClass(mobileSection === "solutions")}
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6" strokeLinecap="round" />
                  </svg>
                </button>
                {mobileSection === "solutions" && (
                  <ul className="ml-1 space-y-0.5 border-l-2 border-[#1c8c80]/30 pl-3">
                    {SOLUTION_LINKS.map((item) => (
                      <li key={item.href}>
                        <Link
                          href={item.href}
                          className="block rounded-md py-2.5 pl-1 text-sm text-ink-muted hover:text-ink"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        href="/solutions"
                        className="block rounded-md py-2.5 pl-1 text-sm font-semibold text-[#1c8c80] hover:text-ink"
                        onClick={() => setMobileOpen(false)}
                      >
                        View all solutions
                      </Link>
                    </li>
                  </ul>
                )}

                <Link
                  href="/use-cases"
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-muted"
                  onClick={() => setMobileOpen(false)}
                >
                  Use cases
                </Link>
                <Link
                  href="/how-we-work"
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-muted"
                  onClick={() => setMobileOpen(false)}
                >
                  How we work
                </Link>
                <Link
                  href="/blogs"
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-muted"
                  onClick={() => setMobileOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  href="/about"
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-muted"
                  onClick={() => setMobileOpen(false)}
                >
                  About
                </Link>
                <Link
                  href="/contact"
                  className="block rounded-lg px-3 py-3 text-base font-medium text-ink hover:bg-muted"
                  onClick={() => setMobileOpen(false)}
                >
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
